import { useState } from "react";
import { format, parseISO } from "date-fns";
import { es as esLocale, ptBR as ptLocale } from "date-fns/locale";
import { Calendar as CalendarIcon, X } from "lucide-react";

import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";

/**
 * Drop-in replacement for a native date input. Accepts and emits ISO YYYY-MM-DD strings.
 * Locale follows the active LangContext (Spanish/English) and shows a "clear" affordance
 * when there's a value.
 */
export function DatePicker({ value, onChange, placeholder, testid, allowClear = true, disabled = false }) {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const date = value ? parseISO(value) : null;

  const handlePick = (d) => {
    if (!d) {
      onChange("");
    } else {
      // Local-date YYYY-MM-DD (avoid TZ drift)
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      onChange(`${yyyy}-${mm}-${dd}`);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={testid}
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal h-10",
            !date && "text-slate-400",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">
            {date ? format(date, "PPP", { locale: tx(lang, undefined, esLocale, ptLocale) }) : (placeholder || "—")}
          </span>
          {allowClear && date && (
            <X
              data-testid={testid ? `${testid}-clear` : undefined}
              className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange("");
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date || undefined}
          onSelect={handlePick}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
